import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { SubscriptionService } from '../lib/subscriptionService';
import { 
  SubscriptionStatus, 
  FeatureAccess, 
  SubscriptionPlan,
  FEATURE_NAMES,
  USAGE_METRICS
} from '../types/subscription';

export interface UseSubscriptionReturn {
  // State
  subscriptionStatus: SubscriptionStatus | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  refreshSubscription: () => Promise<void>;
  checkFeatureAccess: (featureName: string) => boolean;
  checkFeatureLimit: (featureName: string, metricName: string) => Promise<{ allowed: boolean; current: number; limit: number }>;
  incrementUsage: (metricName: string, increment?: number) => Promise<void>;
  
  // Convenience methods
  hasFeature: (featureName: string) => boolean;
  getCurrentUsage: (metricName: string) => number;
  getLimit: (metricName: string) => number;
  isUnlimited: (metricName: string) => boolean;
  
  // Plan information
  currentPlan: SubscriptionPlan | null;
  isProUser: boolean;
  isEnterpriseUser: boolean;
  isFreeUser: boolean;
}

export const useSubscription = (): UseSubscriptionReturn => {
  const { user, isLoaded } = useUser();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSubscriptionStatus = useCallback(async () => {
    if (!user || !isLoaded) return;

    try {
      setIsLoading(true);
      setError(null);
      
      // Ensure user profile exists
      await SubscriptionService.ensureUserProfile(
        user.id,
        user.primaryEmailAddress?.emailAddress || '',
        user.firstName || undefined,
        user.lastName || undefined
      );
      
      const status = await SubscriptionService.getSubscriptionStatus(user.id);
      setSubscriptionStatus(status);
    } catch (err) {
      console.error('Failed to load subscription status:', err);
      setError(err instanceof Error ? err.message : 'Failed to load subscription status');
    } finally {
      setIsLoading(false);
    }
  }, [user, isLoaded]);

  useEffect(() => {
    loadSubscriptionStatus();
  }, [loadSubscriptionStatus]);

  const refreshSubscription = useCallback(async () => {
    await loadSubscriptionStatus();
  }, [loadSubscriptionStatus]);

  const checkFeatureAccess = useCallback((featureName: string): boolean => {
    if (!subscriptionStatus) return false;
    
    const feature = subscriptionStatus.features.find(f => f.feature === featureName);
    return feature?.isEnabled || false;
  }, [subscriptionStatus]);

  const checkFeatureLimit = useCallback(async (featureName: string, metricName: string) => {
    if (!user) return { allowed: false, current: 0, limit: 0 };
    
    const userProfile = await SubscriptionService.getUserProfile(user.id);
    if (!userProfile) return { allowed: false, current: 0, limit: 0 };
    
    return await SubscriptionService.checkFeatureLimit(userProfile.id, featureName, metricName);
  }, [user]);

  const incrementUsage = useCallback(async (metricName: string, increment: number = 1) => {
    if (!user) return;
    
    const userProfile = await SubscriptionService.getUserProfile(user.id);
    if (!userProfile) return;
    
    await SubscriptionService.incrementUsage(userProfile.id, metricName, increment);
    await refreshSubscription(); // Refresh to update usage
  }, [user, refreshSubscription]);

  // Convenience methods
  const hasFeature = useCallback((featureName: string): boolean => {
    return checkFeatureAccess(featureName);
  }, [checkFeatureAccess]);

  const getCurrentUsage = useCallback((metricName: string): number => {
    return subscriptionStatus?.usage[metricName] || 0;
  }, [subscriptionStatus]);

  const getLimit = useCallback((metricName: string): number => {
    if (!subscriptionStatus) return 0;
    
    // Find the feature that has this metric as a limit
    for (const feature of subscriptionStatus.features) {
      if (feature.limits[metricName] !== undefined) {
        return feature.limits[metricName];
      }
    }
    
    return 0;
  }, [subscriptionStatus]);

  const isUnlimited = useCallback((metricName: string): boolean => {
    return getLimit(metricName) === -1;
  }, [getLimit]);

  // Plan information
  const currentPlan = subscriptionStatus?.currentPlan || null;
  const isProUser = currentPlan?.name === 'pro';
  const isEnterpriseUser = currentPlan?.name === 'enterprise';
  const isFreeUser = currentPlan?.name === 'free';

  return {
    // State
    subscriptionStatus,
    isLoading,
    error,
    
    // Actions
    refreshSubscription,
    checkFeatureAccess,
    checkFeatureLimit,
    incrementUsage,
    
    // Convenience methods
    hasFeature,
    getCurrentUsage,
    getLimit,
    isUnlimited,
    
    // Plan information
    currentPlan,
    isProUser,
    isEnterpriseUser,
    isFreeUser
  };
};

// Feature-specific hooks for common use cases
export const useFeatureAccess = (featureName: string) => {
  const { hasFeature, isLoading } = useSubscription();
  return {
    hasAccess: hasFeature(featureName),
    isLoading
  };
};

export const useUsageLimit = (featureName: string, metricName: string) => {
  const { getCurrentUsage, getLimit, isUnlimited, isLoading } = useSubscription();
  
  return {
    currentUsage: getCurrentUsage(metricName),
    limit: getLimit(metricName),
    isUnlimited: isUnlimited(metricName),
    isLoading
  };
};

// Predefined hooks for common features
export const useReviewsFeature = () => useFeatureAccess(FEATURE_NAMES.REVIEWS);
export const useAnalyticsFeature = () => useFeatureAccess(FEATURE_NAMES.ADVANCED_ANALYTICS);
export const useSlackIntegration = () => useFeatureAccess(FEATURE_NAMES.SLACK_INTEGRATION);
export const useCustomBranding = () => useFeatureAccess(FEATURE_NAMES.CUSTOM_BRANDING);
export const useApiAccess = () => useFeatureAccess(FEATURE_NAMES.API_ACCESS);

// Usage tracking hooks
export const useReviewsUsage = () => useUsageLimit(FEATURE_NAMES.REVIEWS, USAGE_METRICS.REVIEWS_CREATED);
export const useStorageUsage = () => useUsageLimit(FEATURE_NAMES.REVIEWS, USAGE_METRICS.STORAGE_USED_GB); 